import { Chip, Grid, Stack, Typography } from '@mui/material'
import React from 'react'

import { MUIExternalLink } from 'components/common/MUIExternalLink'
import PageSection from 'components/common/PageSection'
import Icon from 'components/Icon'
import { getLogo } from 'components/scenarios/create/status-checks/third-party-presets'
import { EditStatusCheckForm } from 'components/status-checks/edit/EditStatusCheckForm'
import {
  formatStatusCheckTag,
  getMonitorPageUrl
} from 'components/status-checks/status-checks-form-utils'
import { useStatusCheckExternalIntegrationsContext } from 'context/status-checks/StatusCheckExternalIntegrationsContext'
import { useStatusChecksContext } from 'context/status-checks/StatusChecksContext'
import useAuth from 'hooks/use-auth'
import { useGremlinQuery } from 'hooks/use-gremlin-query'
import theme from 'theme'
import { IndividualDatadogMonitor } from 'types/api/datadog/monitors'
import { ServiceStatusCheck } from 'types/api/status-checks/status-check'
import { getDatadogMonitorIdFromIntegration } from 'utils/status-checks/status-check-integrations-util-datadog'

/**
 * This component is necessary to ensure the loading of all External integrations prior to form initialization.
 * TODO: Handle asynchronous form initialization in EditStatusCheckForm
 *
 * @param statusCheck
 * @param serviceName
 * @constructor
 */
export const EditStatusCheckFormContainer: React.FC<{
  statusCheck: ServiceStatusCheck
  serviceName?: string
}> = ({ statusCheck, serviceName }) => {
  const { statusChecks: existingStatusChecks } = useStatusChecksContext()
  const {
    getAvailableExternalIntegrations,
    hasErrorExternalIntegrations,
    isFetchingExternalIntegrations
  } = useStatusCheckExternalIntegrationsContext()

  const externalIntegrations = getAvailableExternalIntegrations(false)

  // get details of datadog monitors for display
  const { currentTeamId } = useAuth()
  const monitorId = getDatadogMonitorIdFromIntegration(statusCheck.integration)
  const isDatadogMonitor =
    statusCheck.integration.observabilityToolType === 'DATADOG' &&
    Boolean(monitorId)
  const domainParam =
    isDatadogMonitor && statusCheck.integration.domain != 'US1'
      ? `&domain=${statusCheck.integration.domain}`
      : ''

  const {
    data: datadogMonitor,
    isFetching: isFetchingDatadogMonitor,
    isError: isErrorFetchingDatadogMonitor
  } = useGremlinQuery<IndividualDatadogMonitor>(
    `/datadog/monitors/${monitorId}?teamId=${currentTeamId}&datadogIntegrationName=${statusCheck?.integration?.name}${domainParam}`,
    {},
    {
      enabled: isDatadogMonitor,
      placeholderData: {} as IndividualDatadogMonitor
    }
  )

  return (
    <PageSection
      hasError={hasErrorExternalIntegrations}
      isLoading={isFetchingExternalIntegrations}
    >
      <EditStatusCheckForm
        existingStatusChecks={existingStatusChecks}
        externalIntegrations={externalIntegrations}
        headerContent={
          isDatadogMonitor &&
          !isFetchingDatadogMonitor &&
          !isErrorFetchingDatadogMonitor &&
          datadogMonitor?.name ? (
            <Stack direction="row" gap={2}>
              <img
                css={{
                  color: theme.colors.gray[1],
                  width: theme.fontSizes[4],
                  height: theme.fontSizes[4]
                }}
                src={getLogo(
                  statusCheck.integration.observabilityToolType.toLowerCase()
                )}
              />
              <Stack gap={1}>
                <Stack direction="row" gap={1}>
                  <Typography>{datadogMonitor?.name}</Typography>
                  {statusCheck.integration.url && (
                    <MUIExternalLink
                      href={getMonitorPageUrl(
                        statusCheck.integration.url,
                        statusCheck.integration.type
                      )}
                    >
                      <Icon icon="arrowUpRightFromSquare" />
                    </MUIExternalLink>
                  )}
                </Stack>
                <Grid container gap={1} p={1} width="100%">
                  {datadogMonitor.tags.map((tag) => (
                    <Grid key={tag} item>
                      <Chip
                        css={{ opacity: 0.7 }}
                        label={formatStatusCheckTag(tag)}
                        size="small"
                      />
                    </Grid>
                  ))}
                </Grid>
              </Stack>
            </Stack>
          ) : null
        }
        serviceName={serviceName}
        statusCheck={statusCheck}
      />
    </PageSection>
  )
}
